import { useState, useEffect } from 'react';
import supabase from '../../lib/supabaseClient';

const TruckerRegistrationApplications = () => {
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all'); // all, pending, approved, rejected
    const [notification, setNotification] = useState(null);
    const [rejectModalOpen, setRejectModalOpen] = useState(false);
    const [selectedApplication, setSelectedApplication] = useState(null);
    const [rejectionReason, setRejectionReason] = useState('');

    useEffect(() => {
        fetchApplications();
        
        // Auto-refresh every 5 minutes
        const intervalId = setInterval(() => {
            fetchApplications();
        }, 5 * 60 * 1000); // 5 minutes
        
        return () => clearInterval(intervalId);
    }, []);

    const fetchApplications = async () => {
        try {
            setLoading(true);
            const { data, error } = await supabase
                .from('movers_application')
                .select('*')
                .order('created_at', { ascending: false });

            if (error) throw error;
            
            setApplications(data || []);
        } catch (error) {
            showNotification('Error loading applications', 'error');
        } finally {
            setLoading(false);
        }
    };

    const showNotification = (message, type = 'success') => {
        setNotification({ message, type });
        setTimeout(() => setNotification(null), 4000);
    };

    const handleApprove = async (application) => {
        try {
            // Step 1: Check if mover already exists in movers_data
            const { data: existingMover, error: checkError } = await supabase
                .from('movers_data')
                .select('id')
                .eq('email', application.email)
                .single();

            // Step 2: Only insert if mover doesn't exist in movers_data
            if (!existingMover) {
                const moverData = {
                    company_name: application.company_name,
                    email: application.email,
                    phone: application.phone,
                    business_address: application.business_address,
                    city: application.city,
                    state: application.state,
                    zip_code: application.zip_code,
                    years_in_business: application.years_in_business,
                    number_of_employees: application.number_of_employees,
                    website_link: application.website_link,
                    google_profile_link: application.google_profile_link,
                    company_description: application.company_description,
                    subscription_plan: 'Professional', // Default plan
                    average_rating: 0,
                    total_reviews: 0,
                    created_at: new Date().toISOString()
                };

                const { error: insertError } = await supabase
                    .from('movers_data')
                    .insert([moverData]);

                if (insertError) {
                    showNotification('Error creating mover profile', 'error');
                    return;
                }
            }

            // Step 3: Delete the application from movers_application table
            const { error: deleteError } = await supabase
                .from('movers_application')
                .delete()
                .eq('id', application.id);

            if (deleteError) {
                showNotification('Mover profile created but error removing application', 'error');
                return;
            }

            showNotification('Application approved and mover profile created successfully', 'success');
            fetchApplications();
        } catch (error) {
            showNotification('Error approving application', 'error');
        }
    };

    const handleReject = async (application) => {
        setSelectedApplication(application);
        setRejectionReason('');
        setRejectModalOpen(true);
    };

    const confirmReject = async () => {
        if (!rejectionReason.trim()) {
            showNotification('Please provide a rejection reason', 'error');
            return;
        }

        try {
            const { error } = await supabase
                .from('movers_application')
                .update({ 
                    status: 'Rejected',
                    reviewed_at: new Date().toISOString(),
                    rejection_reason: rejectionReason.trim()
                })
                .eq('id', selectedApplication.id);

            if (error) throw error;

            showNotification('Application rejected successfully', 'success');
            setRejectModalOpen(false);
            setSelectedApplication(null);
            setRejectionReason('');
            fetchApplications();
        } catch (error) {
            showNotification('Error rejecting application', 'error');
        }
    };

    const filteredApplications = applications.filter(app => {
        if (filter === 'all') return true;
        return app.status.toLowerCase() === filter;
    });

    const pendingCount = applications.filter(app => app.status === 'Pending').length;

    if (loading) {
        return (
            <div className="bg-white rounded-xl p-6 shadow-[0_4px_20px_rgba(0,0,0,0.08)] border border-gray-200">
                <div className="animate-pulse">
                    <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
                    <div className="space-y-4">
                        {[1, 2, 3].map(i => (
                            <div key={i} className="h-32 bg-gray-100 rounded-lg"></div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-xl shadow-[0_4px_20px_rgba(0,0,0,0.08)] border border-gray-200">
            {/* Notification */}
            {notification && (
                <div className={`fixed top-4 right-4 px-6 py-3 rounded-lg shadow-lg z-50 ${
                    notification.type === 'success' ? 'bg-green-500' : 'bg-red-500'
                } text-white`}>
                    {notification.message}
                </div>
            )}

            {/* Rejection Modal */}
            {rejectModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
                        <div className="p-6 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                                <h3 className="text-xl font-bold text-gray-800">Reject Application</h3>
                                <button
                                    onClick={() => {
                                        setRejectModalOpen(false);
                                        setSelectedApplication(null);
                                        setRejectionReason('');
                                    }}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                        
                        <div className="p-6">
                            <div className="mb-4">
                                <p className="text-sm text-gray-600 mb-2">Company: <span className="font-medium text-gray-800">{selectedApplication?.company_name}</span></p>
                                <p className="text-sm text-gray-600">Email: <span className="font-medium text-gray-800">{selectedApplication?.email}</span></p>
                            </div>
                            
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Rejection Reason <span className="text-red-500">*</span>
                            </label>
                            <textarea
                                value={rejectionReason}
                                onChange={(e) => setRejectionReason(e.target.value)}
                                placeholder="Please provide a clear reason for rejection..."
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                                rows="5"
                                autoFocus
                            />
                            <p className="mt-2 text-xs text-gray-500">
                                This reason will be shown to the applicant when they try to sign in.
                            </p>
                        </div>
                        
                        <div className="p-6 bg-gray-50 rounded-b-xl flex gap-3">
                            <button
                                onClick={() => {
                                    setRejectModalOpen(false);
                                    setSelectedApplication(null);
                                    setRejectionReason('');
                                }}
                                className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmReject}
                                disabled={!rejectionReason.trim()}
                                className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed rounded-lg transition-colors"
                            >
                                Reject Application
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Header */}
            <div className="p-6 border-b border-gray-200">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <h2 className="text-xl font-bold text-gray-800">Mover Applications</h2>
                        {pendingCount > 0 && (
                            <span className="px-3 py-1 bg-black text-white text-sm font-medium rounded-lg">
                                {pendingCount} Pending
                            </span>
                        )}
                    </div>
                    
                    {/* Filter Tabs */}
                    <div className="flex bg-gray-100 rounded-lg p-1">
                        {[
                            { key: 'all', label: 'All' },
                            { key: 'pending', label: 'Pending' },
                            { key: 'approved', label: 'Approved' },
                            { key: 'rejected', label: 'Rejected' }
                        ].map(tab => (
                            <button
                                key={tab.key}
                                onClick={() => setFilter(tab.key)}
                                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                                    filter === tab.key
                                        ? 'bg-white text-gray-900 shadow-sm'
                                        : 'text-gray-600 hover:text-gray-900'
                                }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Applications List */}
            <div className="p-6">
                {filteredApplications.length === 0 ? (
                    <div className="text-center py-8">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                        </div>
                        <p className="text-gray-500">No applications found</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {filteredApplications.map((application) => (
                            <TruckerApplicationCard
                                key={application.id}
                                application={application}
                                onApprove={handleApprove}
                                onReject={handleReject}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

const TruckerApplicationCard = ({ application, onApprove, onReject }) => {
    const [showDetails, setShowDetails] = useState(false);

    const getStatusColor = (status) => {
        switch (status.toLowerCase()) {
            case 'pending':
                return 'bg-blue-50 text-blue-700';
            case 'approved':
                return 'bg-green-50 text-green-700';
            case 'rejected':
                return 'bg-red-50 text-red-700';
            default:
                return 'bg-gray-50 text-gray-700';
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
        if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
        if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
        return date.toLocaleDateString();
    };

    return (
        <div className="border border-gray-200 rounded-lg hover:shadow-md transition-all duration-300">
            {/* Main Card Content */}
            <div className="p-5">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    {/* Company Info */}
                    <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                            <h3 className="text-lg font-bold text-gray-800">{application.company_name}</h3>
                        </div>
                        
                        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-2">
                            <span className="flex items-center gap-1">
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                                </svg>
                                {application.email}
                            </span>
                            <span className="flex items-center gap-1">
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                                </svg>
                                {application.phone}
                            </span>
                        </div>
                        
                        <p className="text-xs text-gray-500">Submitted {formatDate(application.created_at)}</p>
                    </div>

                    {/* Status and Actions */}
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                        <span className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusColor(application.status)}`}>
                            {application.status}
                        </span>
                        
                        <button
                            onClick={() => setShowDetails(!showDetails)}
                            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                        >
                            {showDetails ? 'Hide Details' : 'View Details'}
                        </button>
                    </div>
                </div>

                {/* Action Buttons - Only show for pending applications */}
                {application.status === 'Pending' && (
                    <div className="flex flex-col sm:flex-row gap-3 mt-4 pt-4 border-t border-gray-100">
                        <button 
                            onClick={() => onReject(application)}
                            className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors flex items-center justify-center gap-2"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            Reject
                        </button>
                        <button 
                            onClick={() => onApprove(application)}
                            className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors flex items-center justify-center gap-2"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Approve
                        </button>
                    </div>
                )}
            </div>

            {/* Expandable Details */}
            {showDetails && (
                <div className="px-5 pb-5 border-t border-gray-100">
                    <div className="pt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                            <p className="text-gray-600 mb-1">Company Name</p>
                            <p className="font-medium text-gray-800">{application.company_name}</p>
                        </div>
                        <div>
                            <p className="text-gray-600 mb-1">Website</p>
                            <p className="font-medium text-gray-800">{application.website_link || 'N/A'}</p>
                        </div>
                        <div>
                            <p className="text-gray-600 mb-1">Business Address</p>
                            <p className="font-medium text-gray-800">{application.business_address || 'N/A'}</p>
                        </div>
                        <div>
                            <p className="text-gray-600 mb-1">City, State, ZIP</p>
                            <p className="font-medium text-gray-800">
                                {[application.city, application.state, application.zip_code].filter(Boolean).join(', ') || 'N/A'}
                            </p>
                        </div>
                        <div>
                            <p className="text-gray-600 mb-1">Years in Business</p>
                            <p className="font-medium text-gray-800">{application.years_in_business || 'N/A'}</p>
                        </div>
                        <div>
                            <p className="text-gray-600 mb-1">Number of Employees</p>
                            <p className="font-medium text-gray-800">{application.number_of_employees || 'N/A'}</p>
                        </div>
                        <div className="md:col-span-2">
                            <p className="text-gray-600 mb-1">Google Profile</p>
                            <p className="font-medium text-gray-800">{application.google_profile_link || 'N/A'}</p>
                        </div>
                        <div className="md:col-span-2">
                            <p className="text-gray-600 mb-1">Company Description</p>
                            <p className="font-medium text-gray-800">{application.company_description || 'N/A'}</p>
                        </div>
                        {application.rejection_reason && (
                            <div className="md:col-span-2">
                                <p className="text-gray-600 mb-1">Rejection Reason</p>
                                <p className="font-medium text-red-600">{application.rejection_reason}</p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default TruckerRegistrationApplications;